<template>
  <div>
    <h2 class="font-bold text-lg">Select Sessions</h2>
    <div v-for="sessionGroup of sessionGroups" :key="sessionGroup.group">
      <div class="gap-2 flex items-center">
        <RadioButton
          :input-id="sessionGroup.group"
          :value="sessionGroup.group"
          name="sessionGroup"
          :model-value="selectedSessionGroup"
          @change="handleSessionGroupChangeRequest(sessionGroup)"
        />
        <label :for="sessionGroup.group">{{ sessionGroup.label }}</label>
      </div>
      <div
        v-for="subsession of sessionGroup.subsessions"
        :key="subsession.value"
        class="pl-4 gap-2 flex items-center"
      >
        <Checkbox
          v-model="selectedSubsessions"
          :input-id="subsession.value"
          :value="subsession.value"
          name="subsession"
          :disabled="sessionGroup.group !== selectedSessionGroup"
        />
        <label :for="subsession.value">{{ subsession.label }}</label>
      </div>
    </div>
  </div>
  <ChangeSessionGroup
    v-model:visible="confirmDialogVisible"
    @cancel="cancelChange"
    @continue="confirmChange"
  />
</template>

<script setup lang="ts">
import { ref, watchEffect, watch } from 'vue'
import { useTimetableStore } from '../../../store/timetable'
import ChangeSessionGroup from '../../Popup/ChangeSessionGroup.vue'
import { FIRST_SEM, SECOND_SEM } from '../../../types/constants.types'
import { SessionGroup } from '../../../types/reference_data.types'

const store = useTimetableStore()

const selectedSessionGroup = ref(store.selectedSessionGroup)
const selectedSubsessions = ref(
  Array.isArray(store.selectedSubsessions) ? [...store.selectedSubsessions] : [],
)

const sessionGroups = ref<SessionGroup[]>([])
const confirmDialogVisible = ref(false)
const pendingSessionGroup = ref<SessionGroup | null>(null)

/**
 * @brief Switches the session group, prompting the user to confirm if there is courses that will be deleted as a result
 * @param sessionGroup The session group being changed to
 */
function handleSessionGroupChangeRequest(sessionGroup: SessionGroup) {
  pendingSessionGroup.value = sessionGroup

  if (
    Object.keys(store.selectedCourses[FIRST_SEM]).length > 0 ||
    Object.keys(store.selectedCourses[SECOND_SEM]).length > 0
  )
    confirmDialogVisible.value = true
  else confirmChange()
}

/**
 * @brief Cancels the session change request
 */
function cancelChange() {
  confirmDialogVisible.value = false
  pendingSessionGroup.value = null
}

/**
 * @brief Confirms the session change request, resetting the timetable since the course offerings will change
 */
function confirmChange() {
  if (pendingSessionGroup.value) {
    selectedSessionGroup.value = pendingSessionGroup.value.group
    store.selectedSessionGroup = pendingSessionGroup.value.group

    if (pendingSessionGroup.value.subsessions)
      selectedSubsessions.value = pendingSessionGroup.value.subsessions.map(
        (subsession) => subsession.value,
      )
  }

  confirmDialogVisible.value = false
  pendingSessionGroup.value = null
  store.resetTimetable()
}

watch(
  () => store.selectedSessionGroup,
  (val: string | null) => {
    if (val !== selectedSessionGroup.value) selectedSessionGroup.value = val
  },
)

watch(selectedSubsessions, (val: string[]) => {
  if (JSON.stringify(val) !== JSON.stringify(store.selectedSubsessions))
    store.selectedSubsessions = val
})

watch(
  () => store.selectedSubsessions,
  (val: string[]) => {
    if (JSON.stringify(val) !== JSON.stringify(selectedSubsessions.value))
      selectedSubsessions.value = [...(val || [])]
  },
)

watchEffect(() => {
  void (async () => {
    sessionGroups.value = (await store.getSessions()) || []
  })()
})
</script>
